<section class="cg-admin-country-form">
  <app-admin-page-header>
    <p description>
      @if (isEdit()) {
        Update country details and the public marketing page at
        <a [routerLink]="['/countries', form.slug]" target="_blank" rel="noopener">/countries/{{ form.slug || '…' }}</a>.
        Layout below follows the same top-to-bottom order as
        <a routerLink="/country1" target="_blank" rel="noopener">/country1</a>.
      } @else {
        Create a country and configure its public marketing page (section order matches
        <a routerLink="/country1" target="_blank" rel="noopener">/country1</a>).
      }
    </p>
    <ng-container actions>
      <app-admin-button variant="cancel" type="button" (click)="cancel()">Cancel</app-admin-button>
      @if (!loading() && showSave()) {
        <label class="cg-admin-page-header__status">
          <select
            class="cg-select cg-admin-page-header__status-select"
            [(ngModel)]="form.status"
            name="headerStatus"
            aria-label="Publication status"
          >
            <option value="DRAFT">Draft</option>
            @if (showPublish()) {
              <option value="PUBLISHED">Published</option>
            }
          </select>
        </label>
      }
      @if (showSave()) {
        <app-admin-button variant="accent" type="button" (click)="save()" [disabled]="saving() || loading()">
          {{ saving() ? 'Saving…' : 'Save country' }}
        </app-admin-button>
      }
    </ng-container>
  </app-admin-page-header>

  @if (loadError(); as le) {
    <div class="cg-admin-alert cg-admin-alert--warning">{{ le }}</div>
  }
  @if (error(); as err) {
    <div class="cg-admin-alert cg-admin-alert--error">{{ err }}</div>
  }
  @if (saveMessage(); as msg) {
    <div class="cg-admin-alert cg-admin-alert--success">{{ msg }}</div>
  }

  @if (loading()) {
    <div class="cg-admin-country-form__stack">
      @for (i of [1, 2, 3, 4, 5]; track i) {
        <app-admin-skeleton variant="card" />
      }
    </div>
  } @else {
    <div class="cg-admin-country-form__identity cg-admin-form-grid cg-admin-form-grid--3">
      <label class="cg-admin-field">
        <span class="cg-admin-field__label">Name</span>
        <input class="cg-input" [(ngModel)]="form.name" name="name" />
      </label>
      <label class="cg-admin-field">
        <span class="cg-admin-field__label">Slug</span>
        <input class="cg-input cg-input--mono" [(ngModel)]="form.slug" name="slug" />
      </label>
      <label class="cg-admin-field cg-admin-field--compact">
        <span class="cg-admin-field__label">Flag icon (emoji)</span>
        <input class="cg-input" [(ngModel)]="form.flagIcon" name="flagIcon" />
      </label>
    </div>

    <div class="cg-admin-country-form__stack">
      <app-admin-card title="Country settings" icon="globe" subtitle="Listing metadata for country cards and listings">
        <div class="cg-admin-form-grid">
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Short description</span>
            <textarea class="cg-input" rows="2" [(ngModel)]="form.shortDescription" name="shortDescription"></textarea>
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Highlights (comma-separated)</span>
            <input class="cg-input" [(ngModel)]="form.highlightsCsv" name="highlightsCsv" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Avg fees</span>
            <input class="cg-input" [(ngModel)]="form.stats!.avgFees" name="avgFees" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Visa success rate</span>
            <input class="cg-input" [(ngModel)]="form.stats!.visaSuccessRate" name="visaSuccessRate" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Popular courses (comma-separated)</span>
            <input class="cg-input" [(ngModel)]="form.popularCoursesCsv" name="popularCoursesCsv" />
          </label>
        </div>
      </app-admin-card>

      <div class="cg-admin-country-form__flow-head">
        <h2 class="cg-admin-country-form__flow-title">Page content</h2>
        <p class="cg-form-help">
          Edit top to bottom — the same order visitors see on
          <a routerLink="/country1" target="_blank" rel="noopener">/country1</a>
          and <code>/countries/{{ form.slug || 'slug' }}</code>. The site-wide announcement bar is edited under
          <a routerLink="/platform/marketing/home">Marketing home</a>. Leave fields blank to use API defaults.
        </p>
      </div>

      <app-admin-card
        class="cg-admin-country-section cg-admin-country-section--hero"
        title="Hero"
        subtitle="1 · Full-width hero with headline and CTAs"
        icon="image"
      >
        <div class="cg-admin-form-grid">
          <div class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Hero background image</span>
            <p class="cg-form-help" style="margin-bottom: var(--cg-space-3)">
              Full-width background on <code>/countries/{{ form.slug || 'slug' }}</code>.
              @if (isEdit()) {
                Uploads save immediately; use Save country for all other fields.
              } @else {
                Upload here, then click Save country to create the record.
              }
            </p>
            @if (heroPreviewUrl(); as previewUrl) {
              <div class="cg-admin-hero-bg-preview">
                <img [src]="previewUrl" alt="Hero background preview" />
              </div>
            }
            <div class="cg-admin-form-grid cg-admin-form-grid--2" style="align-items: end">
              <label class="cg-admin-field">
                <span class="cg-admin-field__label">Choose image</span>
                <input type="file" class="cg-input" accept="image/*" (change)="onHeroFile($event)" />
              </label>
              <div class="cg-admin-page-header__actions">
                @if (showUpload()) {
                  <app-admin-button
                    variant="accent"
                    type="button"
                    (click)="uploadHero()"
                    [disabled]="!heroFile || heroUploading()"
                  >
                    {{ heroUploading() ? 'Uploading…' : 'Upload hero image' }}
                  </app-admin-button>
                }
                @if (form.heroMediaAssetId && canEdit()) {
                  <app-admin-button variant="secondary" type="button" (click)="clearHero()">Remove</app-admin-button>
                }
              </div>
            </div>
            @if (heroUploadMessage(); as msg) {
              <p class="cg-form-help" style="margin-top: var(--cg-space-2)">{{ msg }}</p>
            }
          </div>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Eyebrow</span>
            <input class="cg-input" [(ngModel)]="form.heroEyebrow" name="heroEyebrow" />
            <span class="cg-form-help">Leave blank for default: [flag] Study in [country name]</span>
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Title</span>
            <input class="cg-input" [(ngModel)]="form.heroTitle" name="heroTitle" />
            <span class="cg-form-help">Leave blank for default: Study in [country name]</span>
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Lede</span>
            <textarea class="cg-input" rows="3" [(ngModel)]="form.heroLede" name="heroLede"></textarea>
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Primary CTA text</span>
            <input class="cg-input" [(ngModel)]="form.heroPrimaryCtaText" name="hpct" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Primary CTA anchor</span>
            <input class="cg-input" [(ngModel)]="form.heroPrimaryCtaAnchor" name="hpca" />
            <span class="cg-form-help">Leave blank for default: #cta</span>
          </label>
        </div>
      </app-admin-card>

      <app-admin-card
        class="cg-admin-country-section cg-admin-country-section--stats"
        title="Stats strip"
        subtitle="2 · Floating stats below the hero"
        icon="bar-chart-3"
      >
        <label class="cg-admin-field">
          <span class="cg-admin-field__label">One per line: value|label|icon</span>
          <textarea class="cg-input cg-input--mono" rows="6" [(ngModel)]="form.pageStatsText" name="pageStatsText"></textarea>
          <span class="cg-form-help">One per line: value|label|icon. Icons (same as /country1): wallet, briefcase, trending, award — or leave icon empty for none.</span>
        </label>
      </app-admin-card>

      <app-admin-card
        class="cg-admin-country-section cg-admin-country-section--why"
        title="Why study here"
        subtitle="3 · Feature cards grid (up to {{ maxWhyCards }} cards)"
        icon="sparkles"
      >
        <div class="cg-admin-form-grid">
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Eyebrow</span>
            <input class="cg-input" [(ngModel)]="form.whyEyebrow" name="whyEyebrow" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Title</span>
            <input class="cg-input" [(ngModel)]="form.whyTitle" name="whyTitle" />
          </label>
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Subtitle</span>
            <input class="cg-input" [(ngModel)]="form.whySubtitle" name="whySubtitle" />
          </label>
        </div>

        <div class="cg-admin-why-cards">
          <div class="cg-admin-why-cards__toolbar">
            <div class="cg-admin-why-cards__toolbar-copy">
              <h3 class="cg-admin-why-cards__list-title">Feature cards</h3>
              <p class="cg-form-help">
                Up to {{ maxWhyCards }} cards · {{ form.whyCards.length }} / {{ maxWhyCards }} used.
                Each card needs a title, description, and image for the public page.
              </p>
            </div>
            @if (canAddWhyCard() && showEdit()) {
              <app-admin-button variant="accent" type="button" (click)="addWhyCard()">Add card</app-admin-button>
            }
          </div>

          @if (whyCardMessage(); as msg) {
            <p class="cg-admin-why-cards__flash">{{ msg }}</p>
          }

          @if (form.whyCards.length === 0) {
            <div class="cg-admin-why-cards__empty">
              <p>No feature cards yet.</p>
              <p class="cg-form-help">Use <strong>Add card</strong> to create up to {{ maxWhyCards }} cards.</p>
            </div>
          }

          @for (card of form.whyCards; track card.key; let i = $index) {
            <article class="cg-admin-story-card">
              <header class="cg-admin-story-card__head">
                <span class="cg-admin-story-card__index">Card {{ i + 1 }}</span>
                @if (showEdit()) {
                  <app-admin-button variant="ghost" type="button" (click)="removeWhyCard(card.key)">Remove</app-admin-button>
                }
              </header>

              @if (whyCardPreview(card.key); as previewUrl) {
                <div class="cg-admin-story-card__preview">
                  <img [src]="previewUrl" alt="Why card preview" />
                </div>
              }

              <div class="cg-admin-story-card__fields">
                <label class="cg-admin-field">
                  <span class="cg-admin-field__label">Title</span>
                  <input class="cg-input" [(ngModel)]="card.title" [name]="'whyTitle' + card.key" />
                </label>
                <label class="cg-admin-field">
                  <span class="cg-admin-field__label">Description</span>
                  <textarea class="cg-textarea" rows="3" [(ngModel)]="card.description" [name]="'whyDesc' + card.key"></textarea>
                </label>
                <label class="cg-admin-field">
                  <span class="cg-admin-field__label">Image</span>
                  <input type="file" class="cg-input" accept="image/*" (change)="onWhyCardFile(card.key, $event)" />
                </label>
                <div class="cg-admin-story-card__upload">
                  @if (showUpload()) {
                    <app-admin-button
                      variant="accent"
                      type="button"
                      (click)="uploadWhyCardImage(card.key)"
                      [disabled]="!hasWhyCardFile(card.key) || whyCardUploadingKey() === card.key"
                    >
                      {{ whyCardUploadingKey() === card.key ? 'Uploading…' : 'Upload image' }}
                    </app-admin-button>
                  }
                  @if (whyCardPreview(card.key) && canEdit()) {
                    <app-admin-button variant="ghost" type="button" (click)="clearWhyCardImage(card.key)">
                      Remove image
                    </app-admin-button>
                  }
                </div>
              </div>
            </article>
          }

          @if (!canAddWhyCard()) {
            <p class="cg-form-help cg-admin-why-cards__max-hint">Maximum of {{ maxWhyCards }} cards reached.</p>
          }
        </div>
      </app-admin-card>

      <app-admin-card
        class="cg-admin-country-section cg-admin-country-section--costs"
        title="Costs"
        subtitle="4 · Cost breakdown (universities are managed separately)"
        icon="wallet"
      >
        <div class="cg-admin-form-grid">
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Section title</span>
            <input class="cg-input" [(ngModel)]="form.costsTitle" name="costsTitle" />
          </label>
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Costs section (JSON)</span>
            <textarea class="cg-input cg-input--mono" rows="18" [(ngModel)]="form.costBlocksJson" name="costBlocksJson"></textarea>
            <span class="cg-form-help">Object with <code>intro</code>, <code>items</code> (line items; icon: wallet, home, shield), and <code>blockedAccount</code> (right card). Legacy array of blocks still works.</span>
          </label>
        </div>
      </app-admin-card>

      <app-admin-card
        class="cg-admin-country-section cg-admin-country-section--scholarships"
        title="Scholarships"
        subtitle="5 · Scholarship cards grid"
        icon="award"
      >
        <label class="cg-admin-field">
          <span class="cg-admin-field__label">Scholarships (JSON)</span>
          <textarea class="cg-input cg-input--mono" rows="5" [(ngModel)]="form.scholarshipsJson" name="scholarshipsJson"></textarea>
        </label>
      </app-admin-card>

      <app-admin-card
        class="cg-admin-country-section cg-admin-country-section--faq"
        title="FAQ"
        subtitle="6 · Accordion at the bottom of the page"
        icon="help-circle"
      >
        <label class="cg-admin-field">
          <span class="cg-admin-field__label">FAQ items (JSON)</span>
          <textarea class="cg-input cg-input--mono" rows="5" [(ngModel)]="form.faqItemsJson" name="faqItemsJson"></textarea>
        </label>
      </app-admin-card>

      <app-admin-card
        class="cg-admin-country-section cg-admin-country-section--seo"
        title="SEO"
        subtitle="Search and social preview for /countries/{{ form.slug || 'slug' }}"
        icon="search"
      >
        <div class="cg-admin-form-grid">
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">SEO title</span>
            <input class="cg-input" [(ngModel)]="form.seo.title" name="seoTitle" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">SEO description</span>
            <textarea class="cg-input" rows="3" [(ngModel)]="form.seo.description" name="seoDescription"></textarea>
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">SEO keywords (comma-separated)</span>
            <input class="cg-input" [(ngModel)]="form.keywordsCsv" name="seoKeywords" />
          </label>
        </div>
      </app-admin-card>
    </div>

    <div class="cg-admin-fab">
      <app-admin-button variant="cancel" type="button" (click)="cancel()">Cancel</app-admin-button>
      @if (showSave()) {
        <label class="cg-admin-page-header__status">
          <select
            class="cg-select cg-admin-page-header__status-select"
            [(ngModel)]="form.status"
            name="fabStatus"
            aria-label="Publication status"
          >
            <option value="DRAFT">Draft</option>
            @if (showPublish()) {
              <option value="PUBLISHED">Published</option>
            }
          </select>
        </label>
        <app-admin-button variant="accent" type="button" (click)="save()" [disabled]="saving()">
          {{ saving() ? 'Saving…' : 'Save country' }}
        </app-admin-button>
      }
    </div>
  }
</section>
